import { Controller } from "@hotwired/stimulus"

// What the padlocks on the builder's own columns say on hover.
const LOCK_CAT = "Only your company can see the cost category on this project. The owner never sees it."
const LOCK_COST = "Only your company can see what each line costs you. The owner sees Total Billings, what they are billed."
const LOCK_CHECKER = "Only your company's admins can see the AI Cost Checker. The owner never does."

export default class extends Controller {
  static targets = ["container", "category", "categoryName", "lineItems", "row", "unitPrice", "scheduled", "status", "grandTotals", "seed",
    "searchInput", "searchCount", "undoBtn", "attachModal", "attachList", "attachTitle", "attachFile",
    "deleteRowModal", "deleteRowText", "removeCatModal", "removeCatText", "keepLinesBtn",
    "lockModal", "lockTitle", "lockMsg", "lockBtn", "codeWarn",
    "unitPrices", "unitPriceRows", "unitPriceRow", "unitPriceSeed"]
  static values = { saveUrl: String, fromTemplate: Boolean, reloadOnSave: Boolean, gcMode: Boolean, autosave: Boolean, readOnly: Boolean,
                    benchmark: Boolean,
                    // The builder's own columns (Starr 2026-10-22): cost category and
                    // total cost show only to the project's own builder company.
                    private: Boolean,
                    // Which columns this person shows, on every project (the gear).
                    columns: Object, settingsUrl: String,
                    // S2-10: the company's cost codes (suggested as you type) and
                    // whether the company locked the grid to them (unknown codes flagged).
                    codeList: Array, codesLocked: Boolean }

  // GC mode: the grid IS the SOV. It seeds itself from the saved rows and
  // autosaves (replace-all, debounced) on every edit — no Save button.
  // Approve flips readOnly and the grid locks.
  connect() {
    if (this.gcModeValue && this.hasSeedTarget) {
      try {
        this._loading = true
        this.loadData(JSON.parse(this.seedTarget.textContent))
      } catch (e) {
        console.error("[sov-editor] seed parse failed", e)
      } finally {
        this._loading = false
      }
    }
    // Unit prices (K8-13): their own rows under the grid, saved with it.
    if (this.gcModeValue && this.hasUnitPriceSeedTarget) {
      try {
        this._loading = true
        this.loadUnitPrices(JSON.parse(this.unitPriceSeedTarget.textContent))
      } catch (e) {
        console.error("[sov-editor] unit price seed parse failed", e)
      } finally {
        this._loading = false
      }
    }
    if (this.gcModeValue && this.autosaveValue && !this.readOnlyValue) {
      this._onInput = () => this._scheduleAutosave()
      this.element.addEventListener("input", this._onInput)
    }
    if (this.gcModeValue) {
      this._undoStack = []
      this._pushUndoState() // baseline = what's on screen now
      this._bindDragAndDrop()
    }
    if (this.readOnlyValue) this._applyReadOnly()
  }

  disconnect() {
    if (this._onInput) this.element.removeEventListener("input", this._onInput)
    if (this._onDragStart) {
      this.element.removeEventListener("dragstart", this._onDragStart)
      this.element.removeEventListener("dragover", this._onDragOver)
      this.element.removeEventListener("drop", this._onDrop)
      this.element.removeEventListener("dragend", this._onDragEnd)
    }
    clearTimeout(this._autosaveTimer)
  }

  // ── Drag & drop: rows within/across categories, categories as blocks ────

  _bindDragAndDrop() {
    this._onDragStart = (e) => {
      const grip = e.target.closest?.("[data-grip]")
      if (!grip || this.readOnlyValue) return
      if (grip.dataset.grip === "row") {
        this._dragRow = grip.closest("[data-sov-editor-target='row']")
        this._dragCat = null
      } else {
        this._dragCat = grip.closest("[data-sov-editor-target='category']")
        this._dragRow = null
      }
      e.dataTransfer.effectAllowed = "move"
      try { e.dataTransfer.setData("text/plain", "sov") } catch (err) { /* IE quirk */ }
    }
    this._onDragOver = (e) => {
      if (this._dragRow) {
        const overRow = e.target.closest?.("[data-sov-editor-target='row']")
        if (overRow && overRow !== this._dragRow) {
          e.preventDefault()
          const r = overRow.getBoundingClientRect()
          const before = e.clientY < r.top + r.height / 2
          overRow.parentNode.insertBefore(this._dragRow, before ? overRow : overRow.nextSibling)
        } else {
          const tbody = e.target.closest?.("[data-sov-editor-target='lineItems']")
          if (tbody) {
            e.preventDefault()
            if (!tbody.contains(this._dragRow)) tbody.appendChild(this._dragRow)
          }
        }
      } else if (this._dragCat) {
        const overCat = e.target.closest?.("[data-sov-editor-target='category']")
        if (overCat && overCat !== this._dragCat) {
          e.preventDefault()
          const r = overCat.getBoundingClientRect()
          const before = e.clientY < r.top + r.height / 2
          overCat.parentNode.insertBefore(this._dragCat, before ? overCat : overCat.nextSibling)
        }
      }
    }
    this._onDrop = (e) => { if (this._dragRow || this._dragCat) { e.preventDefault(); this._finishDrag() } }
    this._onDragEnd = () => this._finishDrag()

    this.element.addEventListener("dragstart", this._onDragStart)
    this.element.addEventListener("dragover", this._onDragOver)
    this.element.addEventListener("drop", this._onDrop)
    this.element.addEventListener("dragend", this._onDragEnd)
  }

  _finishDrag() {
    if (!this._dragRow && !this._dragCat) return
    this._dragRow = null
    this._dragCat = null
    this._recalcGcTotals()
    this._scheduleAutosave()
  }

  // ── Builder SOV padlock (Phelipe 2026-07-21) ─────────────────────────────
  // Locks/unlocks the whole grid; the page re-renders in the new state.
  // Both directions confirm first (Phelipe 2026-08-17) — the padlock carries
  // the popup copy for the flip it's about to make.

  openLockConfirm(e) {
    const d = e.currentTarget.dataset
    this._lockUrl = d.url
    this.lockTitleTarget.textContent = d.confirmTitle
    this.lockMsgTarget.textContent = d.confirmMsg
    this.lockBtnTarget.textContent = d.confirmLabel
    this.lockModalTarget.classList.remove("hidden")
  }

  closeLockConfirm() {
    this.lockModalTarget.classList.add("hidden")
  }

  async confirmLockToggle() {
    if (!this._lockUrl) return
    this.lockBtnTarget.disabled = true
    try {
      const resp = await fetch(this._lockUrl, {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        }
      })
      if (!resp.ok) throw new Error("Could not change the lock")
      window.location.reload()
    } catch {
      this.lockBtnTarget.disabled = false
    }
  }

  // ── Search / filter ──────────────────────────────────────────────────────

  filterRows() {
    const q = (this.hasSearchInputTarget ? this.searchInputTarget.value : "").trim().toLowerCase()
    let shown = 0
    let total = 0
    this.categoryTargets.forEach(cat => {
      let catShown = 0
      const catName = (cat.querySelector("[data-sov-editor-target='categoryName']")?.value || "").toLowerCase()
      cat.querySelectorAll("[data-sov-editor-target='row']").forEach(row => {
        total++
        const txt = ["item", "spec", "description", "unit"].map(f => row.querySelector(`[data-field='${f}']`)?.value || "").join(" ").toLowerCase()
        const hit = !q || txt.includes(q) || catName.includes(q)
        row.style.display = hit ? "" : "none"
        if (hit) { shown++; catShown++ }
      })
      cat.style.display = q && catShown === 0 ? "none" : ""
    })
    if (this.hasSearchCountTarget) this.searchCountTarget.textContent = q ? `${shown} of ${total} shown` : ""
  }

  // ── Collapse / expand categories (persisted per project) ────────────────

  toggleCollapse(event) {
    const cat = event.target.closest("[data-sov-editor-target='category']")
    const body = cat?.querySelector("[data-cat-body]")
    if (!body) return
    const collapsed = body.style.display === "none"
    body.style.display = collapsed ? "" : "none"
    const chev = cat.querySelector("[data-collapse-btn] svg")
    if (chev) chev.style.transform = collapsed ? "" : "rotate(-90deg)"
    this._saveCollapseState()
  }

  _collapseKey() { return `sov-collapsed:${window.location.pathname}` }

  _saveCollapseState() {
    const closed = this.categoryTargets
      .filter(cat => cat.querySelector("[data-cat-body]")?.style.display === "none")
      .map(cat => cat.querySelector("[data-sov-editor-target='categoryName']")?.value || "")
    try { localStorage.setItem(this._collapseKey(), JSON.stringify(closed)) } catch (e) { /* private mode */ }
  }

  _applyCollapseState() {
    if (!this.gcModeValue) return
    let closed = []
    try { closed = JSON.parse(localStorage.getItem(this._collapseKey()) || "[]") } catch (e) { closed = [] }
    if (!closed.length) return
    this.categoryTargets.forEach(cat => {
      const name = cat.querySelector("[data-sov-editor-target='categoryName']")?.value || ""
      if (closed.includes(name)) {
        const body = cat.querySelector("[data-cat-body]")
        if (body) body.style.display = "none"
        const chev = cat.querySelector("[data-collapse-btn] svg")
        if (chev) chev.style.transform = "rotate(-90deg)"
      }
    })
  }

  // ── Spec column collapse (persisted per project, like categories) ───────

  toggleSpecCol() {
    this._specCollapsed = !this._specCollapsed
    try { localStorage.setItem(this._specKey(), this._specCollapsed ? "1" : "") } catch (e) { /* private mode */ }
    this._applySpecCol()
  }

  _specKey() { return `sov-spec-collapsed:${window.location.pathname}` }

  // Collapses the Spec column to a thin strip (inputs hidden, values kept —
  // they still save). Runs after every row/category insertion so new rows
  // respect the state.
  _applySpecCol() {
    if (this._specCollapsed === undefined) {
      try { this._specCollapsed = localStorage.getItem(this._specKey()) === "1" } catch (e) { this._specCollapsed = false }
    }
    const on = this._specCollapsed
    this.containerTarget.querySelectorAll("[data-spec-col]").forEach(th => {
      th.style.width = on ? "36px" : "100px"
      const label = th.querySelector("[data-spec-label]")
      if (label) label.style.display = on ? "none" : ""
      const chev = th.querySelector("[data-spec-toggle] svg")
      if (chev) chev.style.transform = on ? "rotate(180deg)" : ""
    })
    this.containerTarget.querySelectorAll("[data-spec-cell]").forEach(td => {
      td.style.width = on ? "36px" : "100px"
      const inp = td.querySelector("input")
      if (inp) inp.style.display = on ? "none" : ""
    })
    this._applyColumns()
    this._checkCodes()
  }

  // ── Cost codes from the company list (S2-10, 2026-10-23) ──────────────
  // The list suggests codes as you type. With the company's lock on, a code
  // that isn't in the list gets an amber mark and a count by the search box;
  // the line still saves — the mark is the ask to fix it.

  checkCode(event) {
    this._checkCode(event.target)
    this._countUnknownCodes()
  }

  _checkCode(input) {
    if (!this.codesLockedValue || !input) return
    const value = input.value.trim()
    const known = !value || this._codeSet().has(value.toLowerCase())
    input.classList.toggle("sov-cell-warn", !known)
    input.title = known ? "" : "Not in your company's cost-code list"
  }

  _checkCodes() {
    if (!this.gcModeValue || !this.codesLockedValue) return
    this.containerTarget.querySelectorAll("[data-field='spec']").forEach((input) => this._checkCode(input))
    this._countUnknownCodes()
  }

  _countUnknownCodes() {
    if (!this.hasCodeWarnTarget) return
    const n = this.containerTarget.querySelectorAll("[data-field='spec'].sov-cell-warn").length
    this.codeWarnTarget.textContent = n ? `${n} code${n === 1 ? "" : "s"} not in your list` : ""
    this.codeWarnTarget.classList.toggle("hidden", !n)
  }

  _codeSet() {
    if (!this._codes) this._codes = new Set((this.codeListValue || []).map((c) => String(c).toLowerCase()))
    return this._codes
  }

  // A padlock with the app's hover card (shared/lock_hint, info-tip): the
  // same card the finance tabs show, so the hover works at once and follows
  // dark mode (Phelipe 2026-10-23).
  _lockHint(text) {
    return `<span class="inline-flex align-middle cursor-help" data-controller="info-tip" data-tip-width="240px" data-tip-side="right" style="margin-right:4px;">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-3 h-3" style="color:var(--color-gray-400);"><path fill-rule="evenodd" d="M10 1a4.5 4.5 0 0 0-4.5 4.5V9H5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6a2 2 0 0 0-2-2h-.5V5.5A4.5 4.5 0 0 0 10 1Zm3 8V5.5a3 3 0 1 0-6 0V9h6Z" clip-rule="evenodd"/></svg>
      <template><div class="text-[13px] text-gray-700">${text}</div></template>
    </span>`
  }

  // ── Columns (Phelipe 2026-10-22): the gear by the search box ───────────
  // Which columns this person shows, saved on their account for every
  // project. Description always shows; a hidden column keeps its values and
  // still saves.

  _colOn(key) {
    const v = (this.columnsValue || {})[key]
    return v !== false && v !== "false" && v !== 0 && v !== "0"
  }

  _applyColumns() {
    if (!this.gcModeValue) return
    this.containerTarget.querySelectorAll("[data-col]").forEach(el => {
      el.style.display = this._colOn(el.dataset.col) ? "" : "none"
    })
  }

  async toggleColumn(event) {
    const box = event.target
    const next = { ...(this.columnsValue || {}) }
    next[box.dataset.column] = box.checked
    this.columnsValue = next
    this._applyColumns()
    if (!this.settingsUrlValue) return
    try {
      await fetch(this.settingsUrlValue, {
        method: "PUT",
        headers: { "Content-Type": "application/json", "Accept": "application/json",
                   "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content },
        body: JSON.stringify({ setting: { sov_columns: next } })
      })
    } catch (e) { /* the choice still applies on this page */ }
  }

  // ── Undo (session history of saved states) ──────────────────────────────

  _gridState() {
    return this.categoryTargets.map(cat => ({
      id: cat.dataset.categoryId || null,
      name: cat.querySelector("[data-sov-editor-target='categoryName']")?.value || "Uncategorized",
      kind: cat.dataset.categoryKind || "line_item",
      line_items: [...cat.querySelectorAll("[data-sov-editor-target='row']")].map(row => ({
        id: row.dataset.lineId || null,
        item: this._val(row, "item"),
        spec: this._val(row, "spec"),
        description: this._val(row, "description"),
        quantity: this._val(row, "quantity"),
        unit: this._val(row, "unit"),
        unit_price: this._val(row, "unit_price"),
        scheduled_value: this._val(row, "scheduled"),
        attachments: parseInt(row.querySelector("[data-attach-count]")?.textContent, 10) || 0
      }))
    }))
  }

  _pushUndoState() {
    if (!this._undoStack) this._undoStack = []
    const state = this._gridState()
    const json = JSON.stringify(state)
    if (this._undoStack.length && JSON.stringify(this._undoStack[this._undoStack.length - 1]) === json) return
    this._undoStack.push(state)
    if (this._undoStack.length > 30) this._undoStack.shift()
    this._updateUndoBtn()
  }

  undo() {
    if (!this._undoStack || this._undoStack.length < 2) return
    this._undoStack.pop() // current state
    const prev = this._undoStack[this._undoStack.length - 1]
    this._loading = true
    this.loadData(JSON.parse(JSON.stringify(prev)))
    this._loading = false
    this._updateUndoBtn()
    this._setStatus("Restored — saving…")
    clearTimeout(this._autosaveTimer)
    this._autosaveTimer = setTimeout(() => this._autosave(), 400)
  }

  _updateUndoBtn() {
    if (this.hasUndoBtnTarget) this.undoBtnTarget.disabled = !this._undoStack || this._undoStack.length < 2
  }

  // ── Per-line attachments (backup docs) ──────────────────────────────────

  _financeUrl(action) {
    return this.saveUrlValue.replace(/update_project_sov$/, action)
  }

  async openAttachments(event) {
    const row = event.target.closest("[data-sov-editor-target='row']")
    const lineId = row?.dataset.lineId
    if (!lineId) { this._setStatus("Row is still saving — try again in a second"); return }

    this._attachRow = row
    this._attachLineId = lineId
    if (this.hasAttachTitleTarget) {
      this.attachTitleTarget.textContent = this._val(row, "description") || `Line ${this._val(row, "item") || ""}`
    }
    if (this.hasAttachListTarget) this.attachListTarget.innerHTML = `<p class="text-[13px] text-gray-400">Loading…</p>`
    this.attachModalTarget.classList.remove("hidden")

    try {
      const resp = await fetch(`${this._financeUrl("sov_line_attachments")}?line_item_id=${lineId}`, { headers: { Accept: "application/json" } })
      const data = await resp.json()
      this._renderAttachList(data.attachments || [])
    } catch (e) {
      if (this.hasAttachListTarget) this.attachListTarget.innerHTML = `<p class="text-[13px] text-red-600">Couldn't load attachments.</p>`
    }
  }

  closeAttachments() { this.attachModalTarget.classList.add("hidden") }
  stopModalClick(event) { event.stopPropagation() }
  pickAttachment() { if (this.hasAttachFileTarget) this.attachFileTarget.click() }

  async attachmentChosen(event) {
    const files = [...(event.target.files || [])]
    event.target.value = ""
    for (const file of files) {
      const rowEl = document.createElement("div")
      rowEl.className = "flex items-center gap-2 text-[13px] text-gray-500"
      rowEl.innerHTML = `<svg class="w-3.5 h-3.5 animate-spin text-brand" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg><span class="truncate">${this._esc(file.name)}</span>`
      this.attachListTarget.appendChild(rowEl)
      try {
        const presign = await fetch(this._financeUrl("presign_uploads"), {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content },
          body: JSON.stringify({ files: [{ filename: file.name, content_type: file.type || "application/octet-stream", byte_size: file.size }] })
        })
        const pj = await presign.json()
        if (!presign.ok) throw new Error(pj.error || "upload refused")
        const up = pj.uploads[0]
        const put = await fetch(up.upload_url, { method: "PUT", headers: { "Content-Type": up.content_type }, body: file })
        if (!put.ok) throw new Error("storage upload failed")
        const reg = await fetch(this._financeUrl("create_sov_line_attachment"), {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content },
          body: JSON.stringify({ line_item_id: this._attachLineId, cache_name: up.cache_name, filename: file.name })
        })
        const att = await reg.json()
        if (!reg.ok) throw new Error("could not attach")
        rowEl.remove()
        this.attachListTarget.insertAdjacentHTML("beforeend", this._attachItemHTML(att))
        this._bumpAttachBadge(this._attachRow, +1)
      } catch (e) {
        rowEl.innerHTML = `<span class="text-red-600">${this._esc(file.name)} — ${this._esc(e.message || "upload failed")}</span>`
      }
    }
  }

  async deleteAttachment(event) {
    const item = event.target.closest("[data-attachment-id]")
    if (!item) return
    const btn = event.target.closest("button")
    if (!btn.dataset.confirming) {
      btn.dataset.confirming = "1"
      btn.classList.add("text-red-600")
      setTimeout(() => { delete btn.dataset.confirming; btn.classList.remove("text-red-600") }, 2500)
      return
    }
    try {
      const resp = await fetch(this._financeUrl("delete_sov_line_attachment"), {
        method: "DELETE",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content },
        body: JSON.stringify({ id: item.dataset.attachmentId })
      })
      if (resp.ok) {
        item.remove()
        this._bumpAttachBadge(this._attachRow, -1)
        if (this.hasAttachListTarget && !this.attachListTarget.children.length) {
          this.attachListTarget.innerHTML = `<p class="text-[13px] text-gray-400">No attachments yet.</p>`
        }
      }
    } catch (e) { /* leave the row; user can retry */ }
  }

  _renderAttachList(attachments) {
    if (!this.hasAttachListTarget) return
    if (!attachments.length) {
      this.attachListTarget.innerHTML = `<p class="text-[13px] text-gray-400">No attachments yet.</p>`
      return
    }
    this.attachListTarget.innerHTML = attachments.map(a => this._attachItemHTML(a)).join("")
  }

  _attachItemHTML(att) {
    const trash = this.readOnlyValue ? "" :
      `<button type="button" data-action="click->sov-editor#deleteAttachment" title="Remove"
               class="text-gray-400 hover:text-red-600 cursor-pointer" style="flex:0 0 auto;">
         <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd"/></svg>
       </button>`
    return `<div data-attachment-id="${att.id}" class="flex items-center justify-between gap-2 px-2 py-1.5 rounded hover:bg-gray-50">
      <a href="${att.url}" target="_blank" class="text-[13px] text-brand hover:underline truncate" style="flex:1;min-width:0;">${this._esc(att.filename)}</a>
      ${trash}
    </div>`
  }

  _setAttachBadge(row, count) {
    const badge = row.querySelector("[data-attach-count]")
    if (!badge) return
    badge.textContent = count > 0 ? count : ""
    badge.style.display = count > 0 ? "" : "none"
  }

  _bumpAttachBadge(row, delta) {
    if (!row) return
    const badge = row.querySelector("[data-attach-count]")
    const current = parseInt(badge?.textContent, 10) || 0
    this._setAttachBadge(row, Math.max(0, current + delta))
  }

  // ── Add / Remove ──────────────────────────────────────────────────────

  addCategory() {
    const name = `Category ${this.categoryTargets.length + 1}`
    const html = this._categoryHTML(name)
    this.containerTarget.insertAdjacentHTML("beforeend", html)
    this._autoNumberItems()
  }

  // Add a category with a chosen name + kind (line_item | material) — used by
  // the "+ Category" modal in the SOV builder.
  addNamedCategory(name, kind) {
    const label = (name && name.trim()) || `Category ${this.categoryTargets.length + 1}`
    this.containerTarget.insertAdjacentHTML("beforeend", this._categoryHTML(label, kind || "line_item"))
    this._autoNumberItems()
  }

  // Two-step inline confirm (no browser confirm()): first click arms the
  // button ("Remove category?"), a second click within 3s deletes.
  // Removing a category (S3-1, Jason 2026-10-21: the red trash said "Remove
  // category?" and every line went with it, no warning). An empty one just
  // goes; one that holds lines asks, says how many, and offers to keep them
  // in the category next to it. Undo (the arrow by the search box) brings a
  // removed one back.
  removeCategory(event) {
    const btn = event.target.closest("button")
    const cat = btn?.closest("[data-sov-editor-target='category']")
    if (!cat) return
    const rows = [...cat.querySelectorAll("[data-sov-editor-target='row']")].filter((row) => this._rowHasData(row))
    if (!rows.length) {
      cat.remove()
      this._recalcGcTotals()
      this._scheduleAutosave()
      return
    }
    this._removingCat = cat
    const name = cat.querySelector("[data-sov-editor-target='categoryName']")?.value.trim() || "this category"
    const others = this.categoryTargets.filter((c) => c !== cat)
    if (this.hasRemoveCatTextTarget) {
      this.removeCatTextTarget.innerHTML = `<span class="font-medium text-gray-800">${this._escapeHtml(name)}</span> holds ${rows.length} line${rows.length === 1 ? "" : "s"}.`
    }
    if (this.hasKeepLinesBtnTarget) {
      const next = others[0]?.querySelector("[data-sov-editor-target='categoryName']")?.value.trim()
      this.keepLinesBtnTarget.textContent = others.length ? `Keep the lines${next ? ` in ${next}` : ""}` : "Keep the lines"
    }
    if (this.hasRemoveCatModalTarget) this.removeCatModalTarget.classList.remove("hidden")
  }

  cancelRemoveCategory() {
    this._removingCat = null
    if (this.hasRemoveCatModalTarget) this.removeCatModalTarget.classList.add("hidden")
  }

  // Remove the category and its lines.
  confirmRemoveCategory() {
    const cat = this._removingCat
    this.cancelRemoveCategory()
    if (!cat) return
    cat.remove()
    this._recalcGcTotals()
    this._scheduleAutosave()
  }

  // Keep the lines: they move to the category before this one (else the one
  // after); the only category stays and becomes "Uncategorized".
  keepLinesRemoveCategory() {
    const cat = this._removingCat
    this.cancelRemoveCategory()
    if (!cat) return
    const cats = this.categoryTargets
    const i = cats.indexOf(cat)
    const home = cats[i - 1] || cats[i + 1]
    if (!home) {
      const nameEl = cat.querySelector("[data-sov-editor-target='categoryName']")
      if (nameEl) nameEl.value = "Uncategorized"
      this._scheduleAutosave()
      return
    }
    const tbody = home.querySelector("[data-sov-editor-target='lineItems']")
    cat.querySelectorAll("[data-sov-editor-target='row']").forEach((row) => { if (this._rowHasData(row)) tbody.appendChild(row) })
    cat.remove()
    this._autoNumberItems()
    this._recalcGcTotals()
    this._scheduleAutosave()
  }

  _rowHasData(row) {
    return ["description", "spec", "quantity", "unit_price", "scheduled", "cost_amount"].some((f) => this._val(row, f).trim())
  }

  _escapeHtml(v) {
    return String(v ?? "").replace(/[&<>"]/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[ch]))
  }

  addRow(event) {
    const cat = event.target.closest("[data-sov-editor-target='category']")
    const tbody = cat.querySelector("[data-sov-editor-target='lineItems']")
    tbody.insertAdjacentHTML("beforeend", this._rowHTML())
    this._autoNumberItems()
  }

  // Auto-fill the Item column with 1, 2, 3… by row order. Only fills blanks,
  // so any number the user typed is left alone (no validation either way).
  // Also the common hook after every row/category insertion — reapplies the
  // Spec-column collapse state to freshly inserted markup.
  _autoNumberItems() {
    this.containerTarget.querySelectorAll("[data-sov-editor-target='row']").forEach((row, i) => {
      const itemEl = row.querySelector("[data-field='item']")
      if (itemEl && !itemEl.value.trim()) itemEl.value = i + 1
    })
    if (this.gcModeValue) this._applySpecCol()
  }

  removeRow(event) {
    const row = event.target.closest("[data-sov-editor-target='row']")
    if (!row) return

    // Blank template rows delete silently; anything with data gets a styled
    // confirm modal (no browser confirm() — CLAUDE.md). Pages without the
    // modal markup (bid leveling) keep the old immediate behavior.
    const hasData = ["description", "spec", "quantity", "unit_price", "scheduled"].some(f => this._val(row, f))
    if (!hasData || !this.hasDeleteRowModalTarget) {
      this._deleteRow(row)
      return
    }

    this._pendingRemoveRow = row
    if (this.hasDeleteRowTextTarget) {
      const label = this._val(row, "description") || `line ${this._val(row, "item") || ""}`.trim()
      this.deleteRowTextTarget.textContent = `“${label}”`
    }
    this.deleteRowModalTarget.classList.remove("hidden")
  }

  confirmRemoveRow() {
    if (this._pendingRemoveRow) this._deleteRow(this._pendingRemoveRow)
    this.cancelRemoveRow()
  }

  cancelRemoveRow() {
    this._pendingRemoveRow = null
    if (this.hasDeleteRowModalTarget) this.deleteRowModalTarget.classList.add("hidden")
  }

  _deleteRow(row) {
    const wasUnitPrice = row.dataset.sovEditorTarget === "unitPriceRow"
    row.remove()
    // The last unit price gone → the section steps out of the way again.
    if (wasUnitPrice && this.hasUnitPricesTarget && this.hasUnitPriceRowsTarget &&
        !this.unitPriceRowsTarget.querySelector("[data-sov-editor-target='unitPriceRow']")) {
      this.unitPricesTarget.classList.add("hidden")
    }
    this._recalcGcTotals()
    this._scheduleAutosave()
  }

  // ── Auto-calc $/Unit ──────────────────────────────────────────────────

  calcUnit(event) {
    const row = event.target.closest("[data-sov-editor-target='row']")
    if (!row) return
    const amt = parseFloat(row.querySelector("[data-field='est_amount']").value) || 0
    const qty = parseInt(row.querySelector("[data-field='est_quantity']").value, 10) || 0
    const cell = row.querySelector("[data-sov-editor-target='unitPrice']")
    cell.textContent = qty > 0 ? `$${(amt / qty).toFixed(2)}` : "-"
  }

  // ── Auto-calc Scheduled Value (GC mode: Quantity × Unit Price) ──────────

  // 3-way keep-in-sync for Quantity × Unit Price = Scheduled Value.
  // Edit Qty/Unit Price → Scheduled recomputes. Edit Scheduled → Unit Price
  // back-calcs from Qty (or stands alone as a lump sum when there's no Qty).
  recalcRow(event) {
    const row = event.target.closest("[data-sov-editor-target='row']")
    if (!row) return
    const field = event.target.dataset.field
    const numOf = (f) => this._num(row.querySelector(`[data-field='${f}']`)?.value)
    const qEl = row.querySelector("[data-field='quantity']")
    const upEl = row.querySelector("[data-field='unit_price']")
    const schedEl = row.querySelector("[data-field='scheduled']")
    const q = numOf("quantity"), up = numOf("unit_price"), sched = numOf("scheduled")

    if (field === "scheduled") {
      if (q !== null && q !== 0 && sched !== null) upEl.value = this._fmtMoney(sched / q)
      else if (up !== null && up !== 0 && sched !== null && q === null) qEl.value = this._fmtQty(sched / up)
    } else if (q !== null && up !== null) {
      // Total Value displays as dollars-with-cents (1,234.50)
      schedEl.value = this._fmtMoney(q * up)
    }
    this._recalcGcTotals()
  }

  // "1,234.5" / "1234.5" → 1234.5; blank/junk → null.
  _num(str) {
    const v = parseFloat(String(str ?? "").replace(/,/g, ""))
    return Number.isNaN(v) ? null : v
  }

  // Blur on Total Value → settle into dollars-with-cents display.
  formatScheduled(event) {
    const v = this._num(event.target.value)
    if (v !== null) event.target.value = this._fmtMoney(v)
  }

  // Blur on Unit Price → dollars-with-cents ("2,275.00").
  formatUnitPrice(event) {
    const v = this._num(event.target.value)
    if (v !== null) event.target.value = this._fmtMoney(v)
  }

  // Blur on Qty → thousands separators, decimals kept as typed ("15,739",
  // "4,534.5").
  formatQty(event) {
    const v = this._num(event.target.value)
    if (v !== null) event.target.value = this._fmtQty(v)
  }

  _fmtQty(n) {
    return n.toLocaleString("en-US", { maximumFractionDigits: 3 })
  }

  // ── Category subtotals + grand total + document-total checker (GC) ──────

  _fmtMoney(n) {
    return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  _recalcGcTotals() {
    if (!this.gcModeValue) return
    let grand = 0
    this.categoryTargets.forEach(cat => {
      let subtotal = 0
      cat.querySelectorAll("[data-field='scheduled']").forEach(inp => {
        const v = this._num(inp.value) // comma-safe: "49,764.00" → 49764
        if (v !== null) subtotal += v
      })
      grand += subtotal
      const cell = cat.querySelector(".js-cat-subtotal")
      if (cell) cell.textContent = subtotal > 0 ? `$${this._fmtMoney(subtotal)}` : ""
    })

    if (!this.hasGrandTotalsTarget) return
    this.grandTotalsTarget.classList.remove("hidden")

    // Checker: compare our sum against the total printed on the document
    // (set by sov-extract from the AI read; absent for manual/default builds).
    let checker = ""
    const doc = typeof this.documentTotal === "number" ? this.documentTotal : null
    if (doc !== null && grand > 0) {
      if (Math.abs(grand - doc) <= 0.5) {
        checker = `<p class="mt-1.5 text-[13px] text-green-600 flex items-center justify-end gap-1">
          <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd"/></svg>
          It matches — the line items add up to the document's total ($${this._fmtMoney(doc)}).
        </p>`
      } else {
        checker = `<p class="mt-1.5 text-[13px] text-red-600 text-right">
          Something is off — it's not matching the PDF. The line items add up to
          $${this._fmtMoney(grand)}, but the document shows $${this._fmtMoney(doc)}
          (difference $${this._fmtMoney(Math.abs(grand - doc))}).
        </p>`
      }
    }

    // Observations from the AI read (correction notes honored, math errors
    // spotted, unreadable values…) — set by sov-extract after an extraction.
    let observations = ""
    if (Array.isArray(this.observations) && this.observations.length) {
      observations = `
        <div class="mt-3 border border-amber-200 bg-amber-50 rounded-lg px-4 py-3">
          <p class="text-[13px] font-semibold text-amber-800 mb-1">Observations from the AI extraction</p>
          <ul class="list-disc pl-5 space-y-0.5">
            ${this.observations.map(o => `<li class="text-[13px] text-amber-800">${this._esc(o)}</li>`).join("")}
          </ul>
        </div>`
    }

    this.grandTotalsTarget.innerHTML = `
      <div class="flex items-center justify-between px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg">
        <span class="text-sm font-bold text-gray-800">Total</span>
        <span class="text-sm font-bold text-gray-900 tabular-nums">${grand > 0 ? `$${this._fmtMoney(grand)}` : "—"}</span>
      </div>${checker}${observations}`
  }

  _esc(str) {
    const d = document.createElement("div")
    d.textContent = String(str)
    return d.innerHTML
  }

  // ── Save ──────────────────────────────────────────────────────────────

  // Full grid → payload rows (GC columns). Skips rows with nothing in them
  // so blank template rows never persist as empty line items.
  _gcPayload() {
    const lineItems = []
    this._payloadRows = [] // DOM rows in payload order — ids adopted from the save response
    let seq = 0
    this.categoryTargets.forEach(cat => {
      const catName = cat.querySelector("[data-sov-editor-target='categoryName']").value.trim() || "Uncategorized"
      const catKind = cat.dataset.categoryKind || "line_item"
      const catId = cat.dataset.categoryId || ""
      cat.querySelectorAll("[data-sov-editor-target='row']").forEach(row => {
        const li = {
          csi_div: this._val(row, "spec"),
          description: this._val(row, "description"),
          quantity: this._val(row, "quantity"),
          unit: this._val(row, "unit"),
          unit_price: this._val(row, "unit_price"),
          scheduled_value: this._val(row, "scheduled")
        }
        if (this.privateValue) {
          li.cost_category = this._val(row, "cost_category")
          li.cost_amount = this._val(row, "cost_amount")
        }
        if (!li.description && !li.csi_div && !li.quantity && !li.unit_price && !li.scheduled_value && !li.cost_amount) return
        seq++
        lineItems.push({ id: row.dataset.lineId || "", category_id: catId,
                         category_name: catName, category_kind: catKind, sequence: seq,
                         item: this._val(row, "item"), ...li })
        this._payloadRows.push(row)
      })
    })
    return lineItems
  }

  _legacyPayload() {
    const lineItems = []
    let seq = 0
    this.categoryTargets.forEach(cat => {
      const catName = cat.querySelector("[data-sov-editor-target='categoryName']").value.trim() || "Uncategorized"
      cat.querySelectorAll("[data-sov-editor-target='row']").forEach(row => {
        seq++
        lineItems.push({
          category_name: catName,
          sequence: seq,
          item: this._val(row, "item"),
          csi_div: this._val(row, "csi_div"),
          line_item_type: this._val(row, "line_item_type"),
          description: this._val(row, "description"),
          definition: this._val(row, "definition"),
          unit_of_measurement: this._val(row, "unit_of_measurement"),
          est_amount: this._val(row, "est_amount"),
          est_quantity: this._val(row, "est_quantity"),
          checkbox: row.querySelector("[data-field='checkbox']")?.checked || false
        })
      })
    })
    return lineItems
  }

  // Debounced autosave (GC grid): every edit lands here; the grid's full
  // contents replace the saved SOV so what you see is what's stored.
  _scheduleAutosave() {
    if (!this.gcModeValue || !this.autosaveValue || this.readOnlyValue || this._loading) return
    this._setStatus("Saving…")
    clearTimeout(this._autosaveTimer)
    this._autosaveTimer = setTimeout(() => this._autosave(), 1200)
  }

  async _autosave() {
    try {
      const resp = await fetch(this.saveUrlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content
        },
        body: JSON.stringify({ line_items: this._gcPayload(), unit_prices: this._unitPricePayload(), replace: "1" })
      })
      if (resp.ok) {
        const data = await resp.json().catch(() => null)
        if (data) this._adoptIds(data)
        this._setStatus("All changes saved")
        this._pushUndoState()
      } else {
        this._setStatus("Couldn't save — check your connection")
      }
    } catch (e) {
      this._setStatus("Couldn't save — check your connection")
    }
  }

  // The sync response returns row ids in payload order + category ids by
  // name; adopt them so the next autosave updates in place (stable ids keep
  // attachments — and later pay apps — anchored).
  _adoptIds(data) {
    const rows = this._payloadRows || []
    ;(data.line_item_ids || []).forEach((id, i) => { if (rows[i]) rows[i].dataset.lineId = id })
    const upRows = this._unitPayloadRows || []
    ;(data.unit_price_ids || []).forEach((id, i) => { if (upRows[i]) upRows[i].dataset.lineId = id })
    const byName = {}
    ;(data.categories || []).forEach(c => { byName[c.name] = c.id })
    this.categoryTargets.forEach(cat => {
      const nm = cat.querySelector("[data-sov-editor-target='categoryName']").value.trim() || "Uncategorized"
      if (byName[nm]) cat.dataset.categoryId = byName[nm]
    })
  }

  // Approved (or view-only) SOV: inputs lock, add/remove controls hide.
  _applyReadOnly() {
    // Locked cells read as plain text (.sov-cell[readonly]); other inputs
    // (search box etc.) keep their look.
    this.element.querySelectorAll("input").forEach(i => {
      i.readOnly = true
      // Out of the tab order too, so no locked cell ever gets the focus ring.
      if (i.classList.contains("sov-cell")) { i.tabIndex = -1; i.blur() }
    })
    this.element.querySelectorAll("[data-action*='removeRow'], [data-action*='removeCategory'], [data-action*='addRow'], [data-action*='addUnitPrice'], [data-action*='removeUnitPrice'], [data-grip]")
      .forEach(b => { b.style.display = "none" })
  }

  async save() {
    const lineItems = this.gcModeValue ? this._gcPayload() : this._legacyPayload()

    this._setStatus("Saving...")

    try {
      const resp = await fetch(this.saveUrlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content
        },
        body: JSON.stringify({ line_items: lineItems })
      })

      if (resp.ok) {
        this._setStatus("Saved")
        this.fromTemplateValue = false
        // Hide template notice
        const notice = this.element.querySelector(".bg-blue-50")
        if (notice) notice.remove()
        if (this.reloadOnSaveValue) { window.location.reload(); return }
      } else {
        const data = await resp.json()
        this._setStatus(`Error: ${data.error || "Save failed"}`)
      }
    } catch (e) {
      this._setStatus("Error: network failure")
    }

    setTimeout(() => this._setStatus(""), 3000)
  }

  // ── Public: seed the grid from data (SOV builder) ───────────────────────

  // Replace the whole grid with the given categories:
  //   [{ name, line_items: [{ item, csi_div, line_item_type, description,
  //      definition, unit_of_measurement, est_amount, est_quantity }] }]
  loadData(categories) {
    this.containerTarget.innerHTML = ""
    // Unit price items the AI read out of the file (K8-13: "rock excavation,
    // if needed, $225/CY, $5,000 minimum") go to their own section under the
    // grid, out of the contract sum; a category left empty by that is dropped.
    const unitPrices = []
    let cats = (categories || []).map(cat => {
      const items = (cat.line_items || []).filter(li => {
        if (!li || !li.unit_price_item) return true
        unitPrices.push(li)
        return false
      })
      const moved = (cat.line_items || []).length - items.length
      return moved && !items.length ? null : { ...cat, line_items: items }
    }).filter(Boolean)
    if (!cats.length) cats = [{ name: "Category 1", line_items: [] }]

    cats.forEach(cat => {
      this.containerTarget.insertAdjacentHTML("beforeend", this._categoryHTML(cat.name || "Uncategorized", cat.kind || "line_item", cat.id))
      const catEl = this.containerTarget.lastElementChild
      const tbody = catEl.querySelector("[data-sov-editor-target='lineItems']")
      tbody.innerHTML = ""
      const rows = cat.line_items || []
      if (rows.length === 0) {
        tbody.insertAdjacentHTML("beforeend", this._rowHTML())
      } else {
        rows.forEach(li => {
          tbody.insertAdjacentHTML("beforeend", this._rowHTML())
          this._fillRow(tbody.lastElementChild, li)
        })
      }
    })
    if (unitPrices.length && this.hasUnitPricesTarget) {
      this.loadUnitPrices(unitPrices.map(li => ({
        item: li.item, description: li.description, unit: li.unit_of_measurement, unit_price: li.unit_price,
        minimum: li.minimum_amount, estimated_quantity: li.est_quantity, max_amount: li.max_amount
      })))
      this.unitPricesTarget.classList.remove("hidden")
    }
    this._autoNumberItems()
    this._recalcGcTotals()
    this._applyCollapseState()
    if (this.hasSearchInputTarget && this.searchInputTarget.value.trim()) this.filterRows()
    if (this.readOnlyValue) this._applyReadOnly()
    // Persist grid fills from Build SOV (extract / manual / default) right
    // away; the initial seed load is guarded by _loading.
    this._scheduleAutosave()
  }

  _fillRow(row, li) {
    const set = (field, val) => {
      const el = row.querySelector(`[data-field='${field}']`)
      if (el && val !== null && val !== undefined) el.value = val
    }
    if (this.gcModeValue) {
      if (li.id) row.dataset.lineId = li.id
      this._setAttachBadge(row, li.attachments || 0)
      set("item", li.item)
      set("spec", li.spec != null ? li.spec : li.csi_div)
      set("description", li.description)
      const qty = this._num(li.quantity != null ? li.quantity : li.est_quantity)
      set("quantity", qty !== null ? this._fmtQty(qty) : null)
      set("unit", li.unit != null ? li.unit : li.unit_of_measurement)
      const up = this._num(li.unit_price)
      set("unit_price", up !== null ? this._fmtMoney(up) : null)
      const sched = this._num(li.scheduled_value != null ? li.scheduled_value : li.est_amount)
      set("scheduled", sched !== null ? this._fmtMoney(sched) : null)
      if (this.privateValue) {
        set("cost_category", li.cost_category)
        const cost = this._num(li.cost_amount)
        set("cost_amount", cost !== null ? this._fmtMoney(cost) : null)
      }
      const schedEl = row.querySelector("[data-field='scheduled']")
      if (schedEl && !schedEl.value) this.recalcRow({ target: row.querySelector("[data-field='quantity']") })
      return
    }
    set("item", li.item)
    set("csi_div", li.csi_div)
    set("line_item_type", li.line_item_type)
    set("description", li.description)
    set("definition", li.definition)
    set("unit_of_measurement", li.unit_of_measurement)
    set("est_amount", li.est_amount)
    set("est_quantity", li.est_quantity)
    this.calcUnit({ target: row.querySelector("[data-field='est_amount']") })
  }

  // ── Unit prices (K8-13) ───────────────────────────────────────────────
  // Work billed only if it's needed, at a rate per unit (rock excavation at
  // $225/CY, $5,000 minimum). Their own rows under the grid: no quantity, no
  // total, never in the contract sum. Saved with every autosave of the grid.

  loadUnitPrices(rows) {
    if (!this.hasUnitPriceRowsTarget) return
    this.unitPriceRowsTarget.innerHTML = ""
    ;(rows || []).forEach(up => {
      this.unitPriceRowsTarget.insertAdjacentHTML("beforeend", this._unitPriceRowHTML())
      this._fillUnitPriceRow(this.unitPriceRowsTarget.lastElementChild, up)
    })
    this._autoNumberUnitPrices()
    if (this.readOnlyValue) this._applyReadOnly()
  }

  // "+ Unit prices" next to "+ Category": reveals the section (hidden while
  // it has no rows) and starts the first row.
  showUnitPrices() {
    if (!this.hasUnitPricesTarget) return
    this.unitPricesTarget.classList.remove("hidden")
    const rows = this.hasUnitPriceRowsTarget ? this.unitPriceRowsTarget.querySelectorAll("[data-sov-editor-target='unitPriceRow']") : []
    if (rows.length === 0) this.addUnitPrice()
    else rows[rows.length - 1].querySelector("[data-field='description']")?.focus()
    this.unitPricesTarget.scrollIntoView({ behavior: "smooth", block: "center" })
  }

  addUnitPrice() {
    if (!this.hasUnitPriceRowsTarget) return
    this.unitPriceRowsTarget.insertAdjacentHTML("beforeend", this._unitPriceRowHTML())
    this._autoNumberUnitPrices()
    this.unitPriceRowsTarget.lastElementChild.querySelector("[data-field='description']")?.focus()
  }

  removeUnitPrice(event) {
    const row = event.target.closest("[data-sov-editor-target='unitPriceRow']")
    if (!row) return
    const hasData = ["description", "unit_price", "minimum", "estimated_quantity", "max_amount"].some(f => this._val(row, f))
    if (!hasData || !this.hasDeleteRowModalTarget) {
      this._deleteRow(row)
      return
    }
    this._pendingRemoveRow = row
    if (this.hasDeleteRowTextTarget) {
      this.deleteRowTextTarget.textContent = `“${this._val(row, "description") || `unit price ${this._val(row, "item")}`.trim()}”`
    }
    this.deleteRowModalTarget.classList.remove("hidden")
  }

  // UP1, UP2… by row order; a number the user typed stays.
  _autoNumberUnitPrices() {
    if (!this.hasUnitPriceRowsTarget) return
    this.unitPriceRowsTarget.querySelectorAll("[data-sov-editor-target='unitPriceRow']").forEach((row, i) => {
      const itemEl = row.querySelector("[data-field='item']")
      if (itemEl && !itemEl.value.trim()) itemEl.value = `UP${i + 1}`
    })
  }

  _unitPricePayload() {
    const out = []
    this._unitPayloadRows = []
    if (!this.hasUnitPriceRowsTarget) return out
    this.unitPriceRowsTarget.querySelectorAll("[data-sov-editor-target='unitPriceRow']").forEach(row => {
      const up = {
        description: this._val(row, "description"),
        unit: this._val(row, "unit"),
        unit_price: this._val(row, "unit_price"),
        minimum: this._val(row, "minimum"),
        estimated_quantity: this._val(row, "estimated_quantity"),
        max_amount: this._val(row, "max_amount")
      }
      if (!up.description && !up.unit_price && !up.minimum && !up.estimated_quantity && !up.max_amount) return
      out.push({ id: row.dataset.lineId || "", item: this._val(row, "item"), ...up })
      this._unitPayloadRows.push(row)
    })
    return out
  }

  _fillUnitPriceRow(row, up) {
    const set = (field, val) => {
      const el = row.querySelector(`[data-field='${field}']`)
      if (el && val !== null && val !== undefined) el.value = val
    }
    if (up.id) row.dataset.lineId = up.id
    set("item", up.item)
    set("description", up.description)
    set("unit", up.unit)
    const price = this._num(up.unit_price)
    set("unit_price", price !== null ? this._fmtMoney(price) : null)
    const min = this._num(up.minimum)
    set("minimum", min !== null ? this._fmtMoney(min) : null)
    const estQty = this._num(up.estimated_quantity)
    set("estimated_quantity", estQty !== null ? this._fmtQty(estQty) : null)
    const max = this._num(up.max_amount)
    set("max_amount", max !== null ? this._fmtMoney(max) : null)
  }

  _unitPriceRowHTML() {
    // Cells: .sov-cell (application.tailwind.css) — plain text at rest, a box on hover,
      // a ring while typing, plain when locked (Phelipe 2026-10-23).
      const s = ""
    const sr = "text-align:right;"
    return `<tr class="border-t border-gray-200" data-sov-editor-target="unitPriceRow">
      <td class="px-2 py-1" style="width:72px;"><input type="text" data-field="item" class="sov-cell"></td>
      <td class="px-2 py-1" style="width:320px;"><input type="text" data-field="description" placeholder="Rock excavation, if needed" class="sov-cell"></td>
      <td class="px-2 py-1" style="width:70px;"><input type="text" data-field="unit" autocomplete="off" placeholder="CY" class="sov-cell"></td>
      <td class="px-2 py-1" style="width:130px;">
        <div style="display:flex;align-items:center;gap:4px;">
          <span style="color:var(--color-gray-400);font-size:13px;">$</span>
          <input type="text" inputmode="decimal" data-field="unit_price" data-action="blur->sov-editor#formatUnitPrice" class="sov-cell sov-cell-r">
        </div>
      </td>
      <td class="px-2 py-1" style="width:150px;">
        <div style="display:flex;align-items:center;gap:4px;">
          <span style="color:var(--color-gray-400);font-size:13px;">$</span>
          <input type="text" inputmode="decimal" data-field="minimum" data-action="blur->sov-editor#formatUnitPrice" class="sov-cell sov-cell-r">
        </div>
      </td>
      <td class="px-2 py-1" style="width:110px;"><input type="text" inputmode="decimal" data-field="estimated_quantity" data-action="blur->sov-editor#formatQty" title="Estimated quantity (optional)" class="sov-cell sov-cell-r"></td>
      <td class="px-2 py-1" style="width:150px;">
        <div style="display:flex;align-items:center;gap:4px;">
          <span style="color:var(--color-gray-400);font-size:13px;">$</span>
          <input type="text" inputmode="decimal" data-field="max_amount" data-action="blur->sov-editor#formatUnitPrice" title="Not to exceed (optional)" class="sov-cell sov-cell-r">
        </div>
      </td>
      <td class="px-2 py-1 text-center" style="width:48px;">
        <button type="button" class="text-red-400 hover:text-red-600" data-action="sov-editor#removeUnitPrice" style="cursor:pointer;" title="Remove"><svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" /></svg></button>
      </td>
    </tr>`
  }

  // ── Helpers ───────────────────────────────────────────────────────────

  _val(row, field) {
    const el = row.querySelector(`[data-field='${field}']`)
    return el ? el.value : ""
  }

  _setStatus(text) {
    if (this.hasStatusTarget) this.statusTarget.textContent = text
  }

  _removeBtnCell() {
    return `<td class="px-3 py-1"><button type="button" class="text-red-400 hover:text-red-600" data-action="sov-editor#removeRow"><svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" /></svg></button></td>`
  }

  _rowHTML() {
    if (this.gcModeValue) {
      // Use theme CSS variables (not hardcoded hex) so the grid follows dark
      // mode: --color-surface-white is #fff in light / #222 in dark, and
      // --color-gray-900 flips near-black ↔ near-white for readable text.
      // Cells: .sov-cell (application.tailwind.css) — plain text at rest, a box on hover,
      // a ring while typing, plain when locked (Phelipe 2026-10-23).
      const s = ""
      const sr = "text-align:right;"
      return `<tr class="border-t border-gray-200" data-sov-editor-target="row">
        <td class="px-2 py-1" style="width:72px;" data-col="item">
          <div style="display:flex;align-items:center;gap:4px;">
            <span draggable="true" data-grip="row" title="Drag to reorder" style="cursor:grab;color:var(--color-gray-400);user-select:none;font-size:12px;letter-spacing:-2px;flex:0 0 auto;">⋮⋮</span>
            <input type="text" data-field="item" class="sov-cell">
          </div>
        </td>
        <td class="px-2 py-1" style="width:100px;" data-spec-cell data-col="cost_code"><input type="text" data-field="spec" autocomplete="off" class="sov-cell"${this.codeListValue?.length ? ' list="sov-cost-codes" data-action="input->sov-editor#checkCode blur->sov-editor#checkCode"' : ""}></td>
        ${this.privateValue ? `<td class="px-2 py-1" style="width:92px;" data-col="cost_category"><input type="text" data-field="cost_category" maxlength="20" autocomplete="off" list="sov-cost-categories" class="sov-cell"></td>` : ""}
        <td class="px-2 py-1" style="width:240px;"><input type="text" data-field="description" class="sov-cell"></td>
        <td class="px-2 py-1" style="width:90px;" data-col="quantity"><input type="text" inputmode="decimal" data-field="quantity" data-action="input->sov-editor#recalcRow blur->sov-editor#formatQty" class="sov-cell sov-cell-r"></td>
        <td class="px-2 py-1" style="width:70px;" data-col="unit"><input type="text" data-field="unit" autocomplete="off" class="sov-cell"></td>
        <td class="px-2 py-1" style="width:120px;" data-col="unit_price">
          <div style="display:flex;align-items:center;gap:4px;">
            <span style="color:var(--color-gray-400);font-size:13px;">$</span>
            <input type="text" inputmode="decimal" data-field="unit_price" data-action="input->sov-editor#recalcRow blur->sov-editor#formatUnitPrice" class="sov-cell sov-cell-r">
          </div>
        </td>
        ${this.privateValue ? `<td class="px-2 py-1" style="width:150px;" data-col="total_cost">
          <div style="display:flex;align-items:center;gap:4px;">
            <span style="color:var(--color-gray-400);font-size:13px;">$</span>
            <input type="text" inputmode="decimal" data-field="cost_amount" data-action="blur->sov-editor#formatScheduled" class="sov-cell sov-cell-r">
          </div>
        </td>` : ""}
        <td class="px-2 py-1" style="width:200px;" data-col="total_value">
          <div style="display:flex;align-items:center;gap:4px;">
            <span style="color:var(--color-gray-400);font-size:13px;">$</span>
            <input type="text" inputmode="decimal" data-field="scheduled" data-action="input->sov-editor#recalcRow blur->sov-editor#formatScheduled" data-sov-editor-target="scheduled" class="sov-cell sov-cell-r" style="font-weight:500;">
          </div>
        </td>
        ${this.benchmarkValue ? `<td class="px-2 py-1 text-center js-benchmark-col" data-benchmark-cell style="width:95px;"></td>` : ""}
        <td class="px-2 py-1" style="width:64px;">
          <div style="display:flex;align-items:center;justify-content:center;gap:8px;">
            <button type="button" data-attach data-action="sov-editor#openAttachments" title="Attachments" class="text-gray-400 hover:text-gray-600" style="position:relative;cursor:pointer;">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-4 h-4"><path fill-rule="evenodd" d="M15.621 4.379a3 3 0 00-4.242 0l-7 7a3 3 0 004.241 4.243h.001l.497-.5a.75.75 0 011.064 1.057l-.498.501-.002.002a4.5 4.5 0 01-6.364-6.364l7-7a4.5 4.5 0 016.368 6.36l-3.455 3.553A2.625 2.625 0 119.52 9.52l3.45-3.451a.75.75 0 111.061 1.06l-3.45 3.451a1.125 1.125 0 001.587 1.595l3.454-3.553a3 3 0 000-4.242z" clip-rule="evenodd"/></svg>
              <span data-attach-count style="display:none;position:absolute;top:-6px;right:-8px;background:#4b5563;color:#fff;border-radius:8px;font-size:9px;line-height:1;padding:2px 4px;min-width:13px;text-align:center;"></span>
            </button>
            <button type="button" class="text-red-400 hover:text-red-600" data-action="sov-editor#removeRow" style="cursor:pointer;"><svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" /></svg></button>
          </div>
        </td>
      </tr>`
    }
    return `<tr class="border-t border-gray-100" data-sov-editor-target="row">
      <td class="px-3 py-1"><input type="checkbox" data-field="checkbox"></td>
      <td class="px-3 py-1"><input type="text" data-field="item" class="sov-input w-20"></td>
      <td class="px-3 py-1"><input type="text" data-field="csi_div" class="sov-input w-20"></td>
      <td class="px-3 py-1"><input type="text" data-field="line_item_type" class="sov-input w-20"></td>
      <td class="px-3 py-1"><input type="text" data-field="description" class="sov-input w-40"></td>
      <td class="px-3 py-1"><input type="text" data-field="definition" class="sov-input w-40"></td>
      <td class="px-3 py-1"><input type="text" data-field="unit_of_measurement" class="sov-input w-20"></td>
      <td class="px-3 py-1"><input type="number" data-field="est_amount" class="sov-input w-24 text-right" step="0.01" data-action="input->sov-editor#calcUnit"></td>
      <td class="px-3 py-1"><input type="number" data-field="est_quantity" class="sov-input w-20 text-right" data-action="input->sov-editor#calcUnit"></td>
      <td class="px-3 py-1 text-right text-gray-500" data-sov-editor-target="unitPrice">-</td>
      <td class="px-3 py-1"><button type="button" class="text-red-400 hover:text-red-600" data-action="sov-editor#removeRow"><svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" /></svg></button></td>
    </tr>`
  }

  _categoryHTML(name, kind = "line_item", id = null) {
    const badge = kind === "material"
      ? `<span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[13px] bg-amber-100 text-amber-800 whitespace-nowrap">Materials</span>`
      : ""
    const headers = this.gcModeValue
      ? `<th class="px-3 py-2 text-left" style="width:72px;" data-col="item">Item</th>
         <th class="px-3 py-2 text-left" style="width:100px;" data-spec-col data-col="cost_code">
           <span style="display:inline-flex;align-items:center;gap:2px;">
             <span data-spec-label style="white-space:nowrap;">Cost code</span>
             <button type="button" data-spec-toggle data-action="sov-editor#toggleSpecCol" title="Collapse / expand Cost code column" class="text-gray-400 hover:text-gray-600" style="cursor:pointer;flex:0 0 auto;">
               <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-3.5 h-3.5" style="transition:transform 0.15s;"><path fill-rule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clip-rule="evenodd"/></svg>
             </button>
           </span>
         </th>
         ${this.privateValue ? `<th class="px-3 py-2 text-left" style="width:92px;white-space:nowrap;" data-col="cost_category">${this._lockHint(LOCK_CAT)}Cost cat.</th>` : ""}
         <th class="px-3 py-2 text-left" style="width:240px;">Description</th>
         <th class="px-3 py-2 text-center" style="width:90px;" data-col="quantity">Qty</th>
         <th class="px-3 py-2 text-center" style="width:70px;" data-col="unit">Unit</th>
         <th class="px-3 py-2 text-center" style="width:120px;" data-col="unit_price">Unit Price</th>
         ${this.privateValue ? `<th class="px-3 py-2 text-center" style="width:150px;white-space:nowrap;" data-col="total_cost">${this._lockHint(LOCK_COST)}Total Cost</th>` : ""}
         <th class="px-3 py-2 text-center" style="width:200px;" data-col="total_value">Total Billings</th>
         ${this.benchmarkValue ? `<th class="px-3 py-2 text-center js-benchmark-col" style="width:110px;white-space:nowrap;">${this._lockHint(LOCK_CHECKER)}AI Cost Checker</th>` : ""}
         <th class="px-3 py-2" style="width:64px;"></th>`
      : `<th class="px-3 py-2 w-8"></th>
         <th class="px-3 py-2">Item</th>
         <th class="px-3 py-2">CSI Div</th>
         <th class="px-3 py-2">Type</th>
         <th class="px-3 py-2">Description</th>
         <th class="px-3 py-2">Definition</th>
         <th class="px-3 py-2">UOM</th>
         <th class="px-3 py-2 text-right">Est. Amount</th>
         <th class="px-3 py-2 text-right">Est. Qty</th>
         <th class="px-3 py-2 text-right">$/Unit</th>
         <th class="px-3 py-2 w-8"></th>`

    const gcHeaderTools = this.gcModeValue
      ? `<span draggable="true" data-grip="cat" title="Drag to reorder" style="cursor:grab;color:var(--color-gray-400);user-select:none;font-size:12px;letter-spacing:-2px;margin-right:6px;flex:0 0 auto;">⋮⋮</span>
         <button type="button" data-action="sov-editor#toggleCollapse" data-collapse-btn class="text-gray-400 hover:text-gray-600" style="margin-right:2px;cursor:pointer;flex:0 0 auto;">
           <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-4 h-4" style="transition:transform 0.15s;"><path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clip-rule="evenodd"/></svg>
         </button>`
      : ""
    return `<div class="mb-6 border border-gray-200 rounded-lg" data-sov-editor-target="category" data-category-name="${name}" data-category-kind="${kind}"${id ? ` data-category-id="${id}"` : ""}>
      <div class="flex items-center justify-between bg-gray-50 px-4 py-2 rounded-t-lg border-b border-gray-200">
        <div class="flex items-center gap-2 flex-1 mr-2 min-w-0">
          ${gcHeaderTools}
          ${badge}
          <input type="text" value="${name}" data-sov-editor-target="categoryName"
                 class="font-semibold text-gray-900 bg-transparent border-0 focus:ring-0 p-0 text-sm w-full"
                 placeholder="Category name">
        </div>
        <button type="button" class="text-red-500 hover:text-red-700 text-xs whitespace-nowrap" data-action="sov-editor#removeCategory">
          <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd" /></svg>
        </button>
      </div>
      <div data-cat-body>
        <div class="overflow-x-auto">
          <table class="finance-row-highlight w-full text-sm"${this.gcModeValue ? ' style="table-layout:fixed"' : ""}>
            <thead>
              <tr class="bg-gray-50 text-left text-xs text-gray-500 uppercase tracking-wider">
                ${headers}
              </tr>
            </thead>
            <tbody data-sov-editor-target="lineItems">
              ${this._rowHTML()}
            </tbody>
            ${this.gcModeValue ? `<tfoot>
              <tr class="border-t border-gray-200 bg-gray-50">
                <td colspan="${this.privateValue ? 8 : 6}" class="px-3 py-1.5 text-right text-[13px] font-semibold text-gray-600">Subtotal</td>
                <td class="px-3 py-1.5 text-right text-[13px] font-semibold text-gray-800 tabular-nums js-cat-subtotal"></td>
                ${this.benchmarkValue ? `<td class="js-benchmark-col"></td>` : ""}
                <td></td>
              </tr>
            </tfoot>` : ""}
          </table>
        </div>
        <div class="px-4 py-2 border-t border-gray-100">
          <button type="button" class="text-sm text-brand hover:underline" data-action="sov-editor#addRow">
            + Add Line Item
          </button>
        </div>
      </div>
    </div>`
  }
}
